import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ApiUrl from '../../utils/config';
import { ConfigClient, Gender, Tournament, TournamentClient } from '../../utils/apiClient';
import AppContext from '../../AppContext';
import { FormPage, Field, PersonFields, CheckIcon } from '../../common/components/FormKit';
import type { PersonFieldsValue } from '../../common/components/FormKit';
import { parseApiError } from '../../utils/apiError';
import {
  Child,
  ChildApplication,
  addChild,
  childAgeOn,
  getChildren,
  removeChild,
  updateChild,
} from '../../utils/guardianApi';
import {
  CoachOption,
  applyToTournament,
  cancelApplication,
  getCoaches,
  isRegistrationOpen,
} from '../../utils/participantApplyApi';
import { getTournamentLifecycle } from '../../utils/tournamentLifecycle';
import './Children.scss';

const DEFAULT_RANKS = [
  'Б/р', '3 юн.', '2 юн.', '1 юн.',
  '3 разряд', '2 разряд', '1 разряд',
  'КМС', 'МС', 'МСМК', 'ЗМС', 'Другое',
];

const formatDate = (value: string | Date) => new Date(value).toLocaleDateString('ru-RU');

const pluralYears = (n: number) => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'год';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'года';
  return 'лет';
};

interface ChildFormState extends PersonFieldsValue {
  consent: boolean;
}

const emptyChildForm = (): ChildFormState => ({
  lastName: '',
  firstName: '',
  patronymic: '',
  birthDate: undefined,
  city: '',
  gender: undefined,
  consent: false,
});

interface ApplyFormState {
  tournamentId: string;
  weight: string;
  organization: string;
  coachId: string;
  rank: string;
}

const emptyApplyForm = (tournamentId = ''): ApplyFormState => ({
  tournamentId,
  weight: '',
  organization: '',
  coachId: '',
  rank: 'Б/р',
});

const Children: React.FC = () => {
  const appContext = useContext(AppContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [children, setChildren] = useState<Child[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [coaches, setCoaches] = useState<CoachOption[]>([]);
  const [rankOptions, setRankOptions] = useState<string[]>(DEFAULT_RANKS);

  // Модальное окно анкеты ребёнка (создание / правка)
  const [childModal, setChildModal] = useState<{ open: boolean; editingId?: number }>({ open: false });
  const [childForm, setChildForm] = useState<ChildFormState>(emptyChildForm());
  const [childError, setChildError] = useState('');
  const [childSaving, setChildSaving] = useState(false);

  // Модальное окно заявки
  const [applyChildId, setApplyChildId] = useState<number | null>(null);
  const [applyForm, setApplyForm] = useState<ApplyFormState>(emptyApplyForm());
  const [applyError, setApplyError] = useState('');
  const [applySubmitting, setApplySubmitting] = useState(false);

  const applyRequestHandled = useRef(false);

  const loadChildren = useCallback(async () => {
    try {
      setChildren(await getChildren());
      setLoadError('');
    } catch (ex) {
      setLoadError(parseApiError(ex).message || 'Не удалось загрузить список детей.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadChildren();
    // Турниры, тренеры и разряды нужны для заявки (не критично: при ошибке остаются значения по умолчанию)
    new TournamentClient(ApiUrl, axios).getTournaments().then(setTournaments).catch(() => undefined);
    getCoaches().then(setCoaches).catch(() => undefined);
    new ConfigClient(ApiUrl, axios).getConfig()
      .then((config) => { if (config?.rankOptions?.length) setRankOptions(config.rankOptions); })
      .catch(() => undefined);
  }, [loadChildren]);

  const notify = (message: string) => appContext?.setDisappearingNotification({ message });

  const openTournaments = useMemo(
    () => tournaments.filter((t) => isRegistrationOpen(t) && getTournamentLifecycle(t) !== 'finished'),
    [tournaments]
  );

  const applyChild = children.find((c) => c.id === applyChildId) ?? null;

  const openApplyModal = useCallback((childId: number, tournamentId?: number) => {
    setApplyChildId(childId);
    setApplyForm(emptyApplyForm(tournamentId ? String(tournamentId) : ''));
    setApplyError('');
  }, []);

  // Переход со страницы турнира: /my-children?apply=ID — сразу открываем заявку (если ребёнок один)
  useEffect(() => {
    const applyId = Number(searchParams.get('apply'));
    if (!applyId || applyRequestHandled.current || loading || tournaments.length === 0) return;

    applyRequestHandled.current = true;
    const next = new URLSearchParams(searchParams);
    next.delete('apply');
    setSearchParams(next, { replace: true });

    if (children.length === 1 && openTournaments.some((t) => t.id === applyId)) {
      openApplyModal(children[0].id, applyId);
    } else if (children.length > 1) {
      notify('Выберите ребёнка и нажмите «Подать заявку».');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, loading, tournaments, children, openTournaments]);

  // --- анкета ребёнка ---

  const openAddChild = () => {
    setChildForm(emptyChildForm());
    setChildError('');
    setChildModal({ open: true });
  };

  const openEditChild = (child: Child) => {
    setChildForm({
      lastName: child.lastName,
      firstName: child.firstName,
      patronymic: child.patronymic ?? '',
      birthDate: new Date(child.birthDate),
      city: child.city,
      gender: child.gender,
      consent: true,
    });
    setChildError('');
    setChildModal({ open: true, editingId: child.id });
  };

  const closeChildModal = () => setChildModal({ open: false });

  const handleChildSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const editingId = childModal.editingId;

    if (!childForm.gender) {
      setChildError('Укажите пол ребёнка: он нужен для подбора категории.');
      return;
    }
    if (!childForm.birthDate) {
      setChildError('Укажите дату рождения ребёнка.');
      return;
    }
    if (childAgeOn(childForm.birthDate) >= 18) {
      setChildError('Через кабинет родителя можно добавлять только детей младше 18 лет.');
      return;
    }
    if (editingId == null && !childForm.consent) {
      setChildError('Подтвердите согласие на обработку персональных данных ребёнка.');
      return;
    }

    const payload = {
      lastName: childForm.lastName.trim(),
      firstName: childForm.firstName.trim(),
      patronymic: childForm.patronymic?.trim() || undefined,
      birthDate: childForm.birthDate,
      city: childForm.city.trim(),
      gender: childForm.gender,
    };

    setChildError('');
    setChildSaving(true);
    try {
      if (editingId != null) {
        await updateChild(editingId, payload);
      } else {
        await addChild({ ...payload, personalDataConsent: childForm.consent });
      }
      await loadChildren();
      closeChildModal();
      notify(editingId != null ? 'Анкета ребёнка обновлена.' : 'Ребёнок добавлен.');
    } catch (ex) {
      setChildError(parseApiError(ex).message || 'Не удалось сохранить анкету. Попробуйте ещё раз.');
    } finally {
      setChildSaving(false);
    }
  };

  const handleRemoveChild = async (child: Child) => {
    if (!window.confirm(`Удалить анкету «${child.lastName} ${child.firstName}»?`)) return;
    try {
      await removeChild(child.id);
      await loadChildren();
      notify('Анкета ребёнка удалена.');
    } catch (ex) {
      notify(parseApiError(ex).message || 'Не удалось удалить анкету.');
    }
  };

  // --- заявки ---

  const closeApplyModal = () => {
    setApplyChildId(null);
    setApplyError('');
  };

  const handleApplySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!applyChild || applySubmitting) return;

    const tournamentId = Number(applyForm.tournamentId);
    if (!tournamentId) {
      setApplyError('Выберите турнир.');
      return;
    }
    const weight = parseFloat(applyForm.weight.replace(',', '.'));
    if (!Number.isFinite(weight) || weight <= 0 || weight > 200) {
      setApplyError('Укажите вес в килограммах (от 1 до 200).');
      return;
    }
    if (!applyForm.organization.trim()) {
      setApplyError('Укажите спортивную школу или клуб.');
      return;
    }

    setApplyError('');
    setApplySubmitting(true);
    try {
      await applyToTournament({
        tournamentId,
        personId: applyChild.id,
        weight,
        organization: applyForm.organization.trim(),
        coachId: applyForm.coachId ? parseInt(applyForm.coachId, 10) : undefined,
        rank: applyForm.rank || undefined,
      });
      await loadChildren();
      closeApplyModal();
      notify(`Заявка принята: ${applyChild.firstName} зарегистрирован на турнир.`);
    } catch (ex) {
      setApplyError(parseApiError(ex).message || 'Не удалось подать заявку. Попробуйте ещё раз.');
    } finally {
      setApplySubmitting(false);
    }
  };

  const handleCancelApplication = async (child: Child, application: ChildApplication) => {
    if (!window.confirm(`Отозвать заявку на турнир «${application.tournamentName}» для ${child.firstName}?`)) return;
    try {
      await cancelApplication(application.tournamentId, child.id);
      await loadChildren();
    } catch (ex) {
      notify(parseApiError(ex).message || 'Не удалось отозвать заявку.');
    }
  };

  const tournamentsForChild = (child: Child) =>
    openTournaments.filter((t) => !child.applications.some((a) => a.tournamentId === t.id));

  const availableForApply = applyChild ? tournamentsForChild(applyChild) : [];

  return (
    <div className="children-page">
      <header className="children-page__header">
        <div>
          <h1 className="children-page__title">Мои дети</h1>
          <p className="children-page__lead">
            Заведите анкету ребёнка, подавайте заявки на турниры и следите за его участием.
            Вход в личный кабинет у ребёнка не нужен — всем управляете вы.
          </p>
        </div>
        <button type="button" className="btn" onClick={openAddChild}>Добавить ребёнка</button>
      </header>

      {loading && <div className="children-page__state">Загрузка…</div>}
      {!loading && loadError && <div className="children-page__state children-page__state--error" role="alert">{loadError}</div>}

      {!loading && !loadError && children.length === 0 && (
        <div className="children-page__empty">
          <h2>Пока никого нет</h2>
          <p>Добавьте сына или дочь, чтобы подавать заявки на турниры и видеть, где они выступают.</p>
          <button type="button" className="btn" onClick={openAddChild}>Добавить ребёнка</button>
          <p className="children-page__empty-link">
            Или <Link to="/tournaments">посмотрите ближайшие турниры</Link>.
          </p>
        </div>
      )}

      <div className="children-page__list">
        {children.map((child) => {
          const age = childAgeOn(child.birthDate);
          const canApply = tournamentsForChild(child).length > 0;
          return (
            <section key={child.id} className="child-card">
              <div className="child-card__top">
                <div>
                  <h2 className="child-card__name">
                    {child.lastName} {child.firstName}{child.patronymic ? ` ${child.patronymic}` : ''}
                  </h2>
                  <p className="child-card__meta">
                    {age} {pluralYears(age)} · {formatDate(child.birthDate)} · {child.city}
                    {child.gender === Gender.Male ? ' · мальчик' : child.gender === Gender.Female ? ' · девочка' : ''}
                  </p>
                </div>
                <div className="child-card__actions">
                  <button
                    type="button"
                    className="btn btn--register"
                    disabled={!canApply}
                    title={canApply ? undefined : 'Нет турниров с открытой регистрацией'}
                    onClick={() => openApplyModal(child.id)}
                  >
                    Подать заявку
                  </button>
                  <button type="button" className="btn btn--edit" onClick={() => openEditChild(child)}>Изменить</button>
                  <button type="button" className="btn btn--delete" onClick={() => handleRemoveChild(child)}>Удалить</button>
                </div>
              </div>

              <h3 className="child-card__subtitle">Турниры</h3>
              {child.applications.length === 0 ? (
                <p className="child-card__none">Заявок пока нет.</p>
              ) : (
                <ul className="child-card__apps">
                  {child.applications.map((app) => {
                    const lifecycle = getTournamentLifecycle(app);
                    const canCancel = isRegistrationOpen(app) && lifecycle !== 'finished';
                    return (
                      <li key={app.participantId} className="child-card__app">
                        <div className="child-card__app-main">
                          <Link to={`/tournaments/${app.tournamentId}`} className="child-card__app-name">
                            {app.tournamentName}
                          </Link>
                          <span className="child-card__app-meta">
                            {formatDate(app.startDate)}
                            {app.endDate && formatDate(app.endDate) !== formatDate(app.startDate) ? ` – ${formatDate(app.endDate)}` : ''}
                            {' · '}вес {app.weight} кг
                            {app.rank ? ` · ${app.rank}` : ''}
                          </span>
                        </div>
                        <span className={`child-card__badge child-card__badge--${lifecycle}`}>
                          {lifecycle === 'finished' ? 'Завершён' : lifecycle === 'active' ? 'Идёт' : 'Заявка принята'}
                        </span>
                        {canCancel && (
                          <button
                            type="button"
                            className="btn btn--back child-card__app-cancel"
                            onClick={() => handleCancelApplication(child, app)}
                          >
                            Отозвать
                          </button>
                        )}
                        {lifecycle === 'finished' && (
                          <Link to={`/tournaments/${app.tournamentId}/results`} className="child-card__link">Результаты</Link>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          );
        })}
      </div>

      {/* Анкета ребёнка */}
      {childModal.open && (
        <div className="children-page__overlay" onClick={closeChildModal}>
          <div className="children-page__modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <button type="button" className="children-page__close" onClick={closeChildModal} aria-label="Закрыть">×</button>
            <FormPage
              title={childModal.editingId != null ? 'Анкета ребёнка' : 'Новый ребёнок'}
              variant="bare"
            >
              <form className="form-page__form" onSubmit={handleChildSubmit}>
                {childError && <div className="form-page__alert" role="alert">{childError}</div>}

                <PersonFields
                  idPrefix="child"
                  value={childForm}
                  onChange={(patch) => setChildForm((prev) => ({ ...prev, ...patch }))}
                />

                {childModal.editingId == null && (
                  <div className="form-page__consents">
                    <label className={`form-page__check${childError && !childForm.consent ? ' form-page__check--invalid' : ''}`}>
                      <input
                        className="form-page__check-input"
                        type="checkbox"
                        checked={childForm.consent}
                        onChange={(e) => setChildForm((prev) => ({ ...prev, consent: e.target.checked }))}
                      />
                      <span className="form-page__check-box"><CheckIcon /></span>
                      <span>
                        Я законный представитель ребёнка и даю согласие на обработку его персональных данных на условиях{' '}
                        <Link to="/privacy" target="_blank" rel="noopener noreferrer">Политики конфиденциальности</Link>
                      </span>
                    </label>
                  </div>
                )}

                <div className="form-page__actions">
                  <button type="button" className="btn btn--back" onClick={closeChildModal}>Отмена</button>
                  <button type="submit" className="btn form-page__submit" disabled={childSaving}>
                    {childSaving ? 'Сохранение…' : childModal.editingId != null ? 'Сохранить' : 'Добавить'}
                  </button>
                </div>
              </form>
            </FormPage>
          </div>
        </div>
      )}

      {/* Заявка за ребёнка */}
      {applyChild && (
        <div className="children-page__overlay" onClick={closeApplyModal}>
          <div className="children-page__modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <button type="button" className="children-page__close" onClick={closeApplyModal} aria-label="Закрыть">×</button>
            <FormPage
              title="Заявка на турнир"
              subtitle={`${applyChild.lastName} ${applyChild.firstName}, ${childAgeOn(applyChild.birthDate)} ${pluralYears(childAgeOn(applyChild.birthDate))}`}
              variant="bare"
            >
              <form className="form-page__form" onSubmit={handleApplySubmit}>
                {applyError && <div className="form-page__alert" role="alert">{applyError}</div>}

                <Field label="Турнир" htmlFor="apply-tournament">
                  <select
                    id="apply-tournament"
                    className="form-page__input"
                    value={applyForm.tournamentId}
                    onChange={(e) => setApplyForm((prev) => ({ ...prev, tournamentId: e.target.value }))}
                    required
                  >
                    <option value="">Выберите турнир</option>
                    {availableForApply.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({formatDate(t.startDate)})
                      </option>
                    ))}
                  </select>
                </Field>

                <div className="form-page__row form-page__row--two">
                  <Field label="Вес, кг" htmlFor="apply-weight" hint="Категорию подберём по возрасту, полу и весу">
                    <input
                      id="apply-weight"
                      className="form-page__input"
                      type="number"
                      min="1"
                      max="200"
                      step="0.1"
                      inputMode="decimal"
                      value={applyForm.weight}
                      onChange={(e) => setApplyForm((prev) => ({ ...prev, weight: e.target.value }))}
                      required
                    />
                  </Field>
                  <Field label="Разряд" htmlFor="apply-rank" optional>
                    <select
                      id="apply-rank"
                      className="form-page__input"
                      value={applyForm.rank}
                      onChange={(e) => setApplyForm((prev) => ({ ...prev, rank: e.target.value }))}
                    >
                      <option value="">Без разряда</option>
                      {rankOptions.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </Field>
                </div>

                <div className="form-page__row form-page__row--two">
                  <Field label="Спортивная школа / клуб" htmlFor="apply-organization">
                    <input
                      id="apply-organization"
                      className="form-page__input"
                      type="text"
                      maxLength={100}
                      value={applyForm.organization}
                      onChange={(e) => setApplyForm((prev) => ({ ...prev, organization: e.target.value }))}
                      required
                    />
                  </Field>
                  <Field label="Тренер" htmlFor="apply-coach" optional>
                    <select
                      id="apply-coach"
                      className="form-page__input"
                      value={applyForm.coachId}
                      onChange={(e) => setApplyForm((prev) => ({ ...prev, coachId: e.target.value }))}
                    >
                      <option value="">Не указан</option>
                      {coaches.map((c) => (
                        <option key={c.id} value={c.id}>{c.fullName}</option>
                      ))}
                    </select>
                  </Field>
                </div>

                <div className="form-page__actions">
                  <button type="button" className="btn btn--back" onClick={closeApplyModal}>Отмена</button>
                  <button type="submit" className="btn form-page__submit" disabled={applySubmitting}>
                    {applySubmitting ? 'Отправляем…' : 'Подать заявку'}
                  </button>
                </div>
              </form>
            </FormPage>
          </div>
        </div>
      )}
    </div>
  );
};

export default Children;
